import React, { useEffect, useState } from 'react';
import { packageTotal } from '../Routes/reportData';
import { CATALOG_SOURCE, groupCatalog, lineAmountLabel, lineFromItem, parseSqft, priceLabel, useCatalog } from '../../services/catalog';
import './QuotePricingEditor.css';

// packages: the selected packages, with everything already built on them (services, discount, names)
// onChange: every edit goes straight back to the quote builder, so nothing is lost on Back
// atticSize: from the inspection — per-sq-ft services start at this quantity
const QuotePricingEditor = ({ packages, photos = [], atticSize = '', onChange, onSaveExit, onBack, onContinue }) => {
  const catalog = useCatalog();
  const [catalogSearch, setCatalogSearch] = useState('');
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [selectedPackageId, setSelectedPackageId] = useState(packages[0]?.id);
  const [showServiceCatalog, setShowServiceCatalog] = useState(false);
  const [catalogPackageId, setCatalogPackageId] = useState(null);
  const [editingServiceIdx, setEditingServiceIdx] = useState(null);
  const [editingServiceQuantity, setEditingServiceQuantity] = useState('');
  const [editingServiceRate, setEditingServiceRate] = useState('');
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountType, setDiscountType] = useState('amount'); // 'amount' or 'percent'
  const [discountName, setDiscountName] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [showGallery, setShowGallery] = useState(false);
  const [packageData, setPackageData] = useState(
    packages.reduce((acc, pkg) => {
      // sqft: the attic size this package quotes — starts at the inspection's, editable here
      acc[pkg.id] = { services: [], secondAttic: false, discount: null, sqft: parseSqft(atticSize) ? String(parseSqft(atticSize)) : '', ...pkg };
      return acc;
    }, {})
  );

  useEffect(() => {
    if (onChange) onChange(packageData);
  }, [packageData]); // eslint-disable-line react-hooks/exhaustive-deps -- report edits, not a changing callback

  const currentPackage = packageData[selectedPackageId];

  const handleEditPackageName = (id, name) => {
    setEditingPackageId(id);
    setEditingName(name);
  };

  const handleSavePackageName = (id) => {
    setPackageData(prev => ({
      ...prev,
      [id]: { ...prev[id], name: editingName }
    }));
    setEditingPackageId(null);
  };

  const handleClonePackage = (id) => {
    const pkg = packageData[id];
    const newId = Math.max(...Object.keys(packageData).map(Number)) + 1;
    setPackageData(prev => ({
      ...prev,
      [newId]: { ...pkg, id: newId, name: `${pkg.name} (copy)` }
    }));
  };

  // per-sq-ft lines always follow their package's attic size
  const withSqft = (pkg, sqft) => {
    const n = parseSqft(sqft);
    return {
      ...pkg,
      sqft,
      services: pkg.services.map((svc) => (svc.unit === 'SQFT' && n > 0 ? { ...svc, quantity: n } : svc)),
    };
  };

  // one attic size is shared by every package, except packages marked "second attic", which keep their own
  const handleSqftChange = (id, value) => {
    const sqft = value.replace(/[^\d.+,]/g, '');
    setPackageData(prev => {
      const next = { ...prev };
      const targets = prev[id].secondAttic ? [id] : Object.keys(prev).filter((k) => !prev[k].secondAttic);
      targets.forEach((k) => {
        next[k] = withSqft(prev[k], sqft);
      });
      return next;
    });
  };

  const handleToggleSecondAttic = (id) => {
    setPackageData(prev => {
      const pkg = prev[id];
      if (!pkg.secondAttic) return { ...prev, [id]: { ...pkg, secondAttic: true } };
      // back to the shared attic: take the size the other packages use
      const shared = Object.values(prev).find((p) => p.id !== pkg.id && !p.secondAttic);
      return { ...prev, [id]: withSqft({ ...pkg, secondAttic: false }, shared ? shared.sqft : pkg.sqft) };
    });
  };

  const handleAddService = (id) => {
    setCatalogPackageId(id);
    setShowServiceCatalog(true);
  };

  // a catalog item (Zoho Products) becomes a quote line at today's price
  const handleAddServiceToPackage = (item) => {
    const line = lineFromItem(item, packageData[catalogPackageId].sqft);
    setPackageData(prev => ({
      ...prev,
      [catalogPackageId]: {
        ...prev[catalogPackageId],
        services: [...prev[catalogPackageId].services, line]
      }
    }));
  };

  const handleOpenServiceEdit = (idx) => {
    const service = currentPackage.services[idx];
    setEditingServiceIdx(idx);
    setEditingServiceQuantity(String(service.quantity || 1));
    setEditingServiceRate(String(service.unitRate || 0));
  };

  const handleSaveServiceEdit = () => {
    const quantity = parseFloat(editingServiceQuantity) || 1;
    const unitRate = parseFloat(editingServiceRate) || 0;

    setPackageData(prev => ({
      ...prev,
      [selectedPackageId]: {
        ...prev[selectedPackageId],
        services: prev[selectedPackageId].services.map((svc, idx) =>
          idx === editingServiceIdx
            ? { ...svc, quantity, unitRate }
            : svc
        )
      }
    }));

    setEditingServiceIdx(null);
    setEditingServiceQuantity('');
    setEditingServiceRate('');
  };

  const handleRemoveServiceFromEdit = () => {
    setPackageData(prev => ({
      ...prev,
      [selectedPackageId]: {
        ...prev[selectedPackageId],
        services: prev[selectedPackageId].services.filter((_, i) => i !== editingServiceIdx)
      }
    }));

    setEditingServiceIdx(null);
    setEditingServiceQuantity('');
    setEditingServiceRate('');
  };

  const handleOpenDiscountModal = () => {
    const current = currentPackage.discount;
    if (current) {
      setDiscountName(current.name || '');
      setDiscountType(current.type || 'amount');
      setDiscountValue(String(current.value || ''));
    } else {
      setDiscountName('');
      setDiscountType('amount');
      setDiscountValue('');
    }
    setShowDiscountModal(true);
  };

  const handleApplyPresetDiscount = (preset) => {
    setDiscountName(preset.name);
    setDiscountType(preset.type);
    setDiscountValue(String(preset.value));
  };

  const handleSaveDiscount = () => {
    if (!discountName || !discountValue) {
      setShowDiscountModal(false);
      return;
    }

    setPackageData(prev => ({
      ...prev,
      [selectedPackageId]: {
        ...prev[selectedPackageId],
        discount: {
          name: discountName,
          type: discountType,
          value: parseFloat(discountValue) || 0
        }
      }
    }));

    setShowDiscountModal(false);
  };

  const handleRemoveDiscount = () => {
    setPackageData(prev => ({
      ...prev,
      [selectedPackageId]: {
        ...prev[selectedPackageId],
        discount: null
      }
    }));

    setShowDiscountModal(false);
  };

  // Calculate totals
  const calculateSubtotal = (services) => {
    return services.reduce((sum, service) => sum + ((service.quantity || 1) * (service.unitRate || 0)), 0);
  };

  const subtotal = calculateSubtotal(currentPackage?.services || []);

  // Calculate discount amount
  let discountAmount = 0;
  if (currentPackage?.discount) {
    const disc = currentPackage.discount;
    if (disc.type === 'amount') {
      discountAmount = disc.value || 0;
    } else if (disc.type === 'percent') {
      discountAmount = (subtotal * (disc.value || 0)) / 100;
    }
  }

  const subtotalAfterDiscount = subtotal - discountAmount;
  const hstRate = 0.13;
  const hst = subtotalAfterDiscount * hstRate;
  const total = subtotalAfterDiscount + hst;

  // Calculate all packages total
  const allPackagesTotal = Object.values(packageData).reduce((sum, pkg) => {
    const pkgSubtotal = calculateSubtotal(pkg.services || []);
    let pkgDiscount = 0;
    if (pkg.discount) {
      if (pkg.discount.type === 'amount') {
        pkgDiscount = pkg.discount.value || 0;
      } else if (pkg.discount.type === 'percent') {
        pkgDiscount = (pkgSubtotal * (pkg.discount.value || 0)) / 100;
      }
    }
    const pkgSubtotalAfterDiscount = pkgSubtotal - pkgDiscount;
    return sum + (pkgSubtotalAfterDiscount * (1 + hstRate));
  }, 0);

  const handleCloseServiceCatalog = () => {
    setShowServiceCatalog(false);
    setCatalogPackageId(null);
    setCatalogSearch('');
  };

  const handleGoBack = () => onBack(Object.values(packageData));

  const handleContinue = () => onContinue(packageData);

  return (
    <div className="quote-pricing-editor">
      {/* HEADER */}
      <div className="editor-header">
        <button className="editor-back-btn" onClick={handleGoBack}>← Back</button>
        <div className="editor-header-content">
          <div className="editor-step-label">NEW QUOTE</div>
          <h2>Review quote</h2>
        </div>
      </div>

      {/* CONTENT */}
      <div className="editor-content">
        {/* PACKAGES TABS */}
        <div className="packages-tabs">
          {Object.values(packageData).map((pkg) => (
            <button
              key={pkg.id}
              className={`package-tab ${selectedPackageId === pkg.id ? 'active' : ''}`}
              onClick={() => setSelectedPackageId(pkg.id)}
            >
              <div className="tab-name">{pkg.name}</div>
              {/* the package's real total (after discount + HST), same number the report shows */}
              <div className="tab-price">
                {(pkg.services || []).length ? `$${packageTotal(pkg).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'No services yet'}
              </div>
            </button>
          ))}
        </div>

        {/* PACKAGE EDITOR */}
        {currentPackage && (
          <div className="package-editor">
            {/* PACKAGE NAME EDIT */}
            <div className="package-name-row">
              {editingPackageId === selectedPackageId ? (
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  className="package-name-input"
                  onBlur={() => handleSavePackageName(selectedPackageId)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSavePackageName(selectedPackageId)}
                  autoFocus
                />
              ) : (
                <>
                  <button
                    className="btn-edit-name"
                    onClick={() => handleEditPackageName(selectedPackageId, currentPackage.name)}
                    title="Edit package name"
                  >
                    📍
                  </button>
                  <h3>{currentPackage.name}</h3>
                </>
              )}
              <button
                className="btn-clone"
                onClick={() => handleClonePackage(selectedPackageId)}
                title="Clone this package"
              >
                CLONE
              </button>
            </div>

            {/* ATTIC SIZE — every per-sq-ft service in this package uses it */}
            <div className="sqft-row">
              <label className="toggle-left" htmlFor={`sqft-${selectedPackageId}`}>
                <div className="toggle-title">Attic size (sq ft)</div>
                <div className="toggle-help">
                  {atticSize ? 'From the inspection — ' : ''}Top-up, removal and other per-sq-ft services use this
                </div>
              </label>
              <input
                id={`sqft-${selectedPackageId}`}
                className="sqft-input"
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={currentPackage.sqft || ''}
                onChange={(e) => handleSqftChange(selectedPackageId, e.target.value)}
              />
            </div>

            {/* SECOND ATTIC TOGGLE */}
            <div className="second-attic-row">
              <div className="toggle-left">
                <div className="toggle-title">Second attic</div>
                <div className="toggle-help">Check to keep this quote's sqft separate from the others</div>
              </div>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id={`second-attic-${selectedPackageId}`}
                  checked={currentPackage.secondAttic}
                  onChange={() => handleToggleSecondAttic(selectedPackageId)}
                />
                <label htmlFor={`second-attic-${selectedPackageId}`}></label>
              </div>
            </div>

            {/* ADD SERVICE BUTTON */}
            <div className="add-service-row">
              <button
                className="btn-add-service"
                onClick={() => handleAddService(selectedPackageId)}
              >
                <span className="plus-icon">+</span>
                <div className="service-text">
                  <div className="service-title">Add service to {currentPackage.name}</div>
                  <div className="service-subtitle">Browse {catalog.items.length} services in the catalog</div>
                </div>
                <span className="arrow-icon">›</span>
              </button>
            </div>

            {/* SERVICES LIST - GROUPED BY CATEGORY */}
            {currentPackage.services.length > 0 ? (
              <div className="services-list">
                {(() => {
                  // Group services by category
                  const grouped = {};
                  currentPackage.services.forEach((service, idx) => {
                    const category = service.category || 'Other Services';
                    if (!grouped[category]) {
                      grouped[category] = [];
                    }
                    grouped[category].push({ ...service, idx });
                  });

                  // Calculate category totals and sort by first service order
                  const categoryOrder = currentPackage.services.reduce((acc, s, idx) => {
                    const cat = s.category || 'Other Services';
                    if (!acc.includes(cat)) acc.push(cat);
                    return acc;
                  }, []);

                  return categoryOrder.map((category, catIdx) => {
                    const categoryServices = grouped[category];
                    const categoryTotal = categoryServices.reduce((sum, s) => sum + ((s.quantity || 1) * (s.unitRate || 0)), 0);

                    return (
                      <div key={category} className="service-category">
                        <div className="category-header">
                          <div className="category-info">
                            <span className="category-number">{catIdx + 1}</span>
                            <span className="category-name">{category}</span>
                          </div>
                          <div className="category-total">{lineAmountLabel(categoryTotal)}</div>
                        </div>

                        <div className="category-services">
                          {categoryServices.map((service) => (
                            <div key={service.idx} className="service-item">
                              <div className="service-item-header">
                                <h4 className="service-item-name">{service.name}</h4>
                                <div className="service-item-price">
                                  {lineAmountLabel((service.quantity || 1) * (service.unitRate || 0))}
                                </div>
                              </div>
                              <div className="service-item-meta">
                                <span className="service-formula">
                                  {service.quantity || 1} {service.unit || 'EA'} × ${(service.unitRate || 0).toFixed(2)}
                                </span>
                                <button
                                  className="btn-edit-service"
                                  onClick={() => handleOpenServiceEdit(service.idx)}
                                  title="Edit service"
                                >
                                  ✏️ EDIT
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            ) : (
              <div className="services-empty">
                <p>No services added yet</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PRICING SUMMARY */}
      {currentPackage && (
        <div className="pricing-summary">
          <div className="pricing-row">
            <span className="pricing-label">Subtotal</span>
            <span className="pricing-value">${subtotal.toFixed(2)}</span>
          </div>

          {currentPackage.discount ? (
            <>
              <button className="btn-discount-line" onClick={handleOpenDiscountModal}>
                <div className="discount-line-left">
                  <span className="discount-icon-active">🟢</span>
                  <span className="discount-name-active">
                    {currentPackage.discount.name}
                    {currentPackage.discount.type === 'percent' ? ` (${currentPackage.discount.value}%)` : ''}
                  </span>
                </div>
                <span className="discount-amount-active">-${discountAmount.toFixed(2)}</span>
              </button>
            </>
          ) : (
            <button className="btn-add-discount" onClick={handleOpenDiscountModal}>
              + Add discount
            </button>
          )}

          <div className="pricing-divider"></div>

          <div className="pricing-row">
            <span className="pricing-label">ON HST (13%)</span>
            <span className="pricing-value">${hst.toFixed(2)}</span>
          </div>

          <div className="pricing-divider"></div>

          <div className="pricing-total-section">
            <div className="pricing-total-label">
              <span>{currentPackage.name.toUpperCase()} · TOTAL</span>
              <span className="pricing-all-total">ALL</span>
            </div>
            <div className="pricing-total-amount">
              <span className="pricing-total-primary">${total.toFixed(2)}</span>
              <span className="pricing-total-all">${allPackagesTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div className="editor-footer">
        <div className="footer-top">
          <button className="btn-gallery" onClick={() => setShowGallery(true)}>
            🖼️ View gallery{photos.length ? ` (${photos.length})` : ''}
          </button>
          {onSaveExit && (
            <button className="btn-save-exit" onClick={onSaveExit}>Save & exit — send later</button>
          )}
        </div>
        <button className="btn-present" onClick={handleContinue}>
          ⭕ Present to customer
        </button>
      </div>

      {/* DISCOUNT MODAL */}
      {showDiscountModal && (
        <div className="discount-overlay">
          <div className="discount-modal">
            <div className="discount-modal-content">
              <h3 className="discount-title">Discount</h3>
              <p className="discount-subtitle">Applied to {currentPackage.name} · before HST</p>

              {/* QUICK PRESETS */}
              <div className="discount-presets-section">
                <h4 className="discount-section-title">QUICK PRESETS</h4>
                <div className="discount-presets-grid">
                  {[
                    { name: 'Lawn Sign Discount', type: 'amount', value: 250 },
                    { name: 'Seasonal Promotion', type: 'percent', value: 10 },
                    { name: 'Referral Discount', type: 'amount', value: 200 },
                    { name: 'Senior / First Responder', type: 'percent', value: 10 },
                    { name: 'Multi-Service Bundle', type: 'percent', value: 10 },
                    { name: 'Repeat Customer', type: 'percent', value: 5 },
                    { name: 'HVAC Combo', type: 'amount', value: 400 },
                    { name: 'Furnace + Heat Pump Combo', type: 'amount', value: 500 },
                    { name: 'Water Heater Pairing', type: 'amount', value: 200 },
                  ].map((preset, idx) => (
                    <button
                      key={idx}
                      className="preset-btn"
                      onClick={() => handleApplyPresetDiscount(preset)}
                    >
                      <span className="preset-name">{preset.name}</span>
                      <span className="preset-value">
                        {preset.type === 'amount' ? `$${preset.value.toFixed(2)}` : `${preset.value}%`}
                      </span>
                    </button>
                  ))}
                  <button className="preset-btn preset-custom">Custom</button>
                </div>
              </div>

              {/* DISCOUNT NAME */}
              <div className="discount-form-group">
                <label>DISCOUNT NAME</label>
                <input
                  type="text"
                  placeholder="e.g. Lawn Sign Discount"
                  value={discountName}
                  onChange={(e) => setDiscountName(e.target.value)}
                  className="discount-input"
                />
              </div>

              {/* TYPE */}
              <div className="discount-form-group">
                <label>TYPE</label>
                <div className="discount-type-buttons">
                  <button
                    className={`discount-type-btn ${discountType === 'amount' ? 'active' : ''}`}
                    onClick={() => setDiscountType('amount')}
                  >
                    $ Amount
                  </button>
                  <button
                    className={`discount-type-btn ${discountType === 'percent' ? 'active' : ''}`}
                    onClick={() => setDiscountType('percent')}
                  >
                    % Percent
                  </button>
                </div>
              </div>

              {/* AMOUNT OFF */}
              <div className="discount-form-group">
                <label>AMOUNT OFF</label>
                <div className="discount-input-with-icon">
                  <span className="discount-icon">
                    {discountType === 'amount' ? '$' : '%'}
                  </span>
                  <input
                    type="number"
                    min="0"
                    step={discountType === 'amount' ? '0.01' : '0.1'}
                    inputMode="decimal"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="discount-input discount-amount-input"
                  />
                </div>
              </div>

              {/* DISCOUNT APPLIED */}
              <div className="discount-applied-box">
                <span>Discount applied</span>
                <span className="discount-applied-amount">
                  {discountAmount > 0 ? `-$${discountAmount.toFixed(2)}` : '-$0.00'}
                </span>
              </div>
            </div>

            <div className="discount-modal-footer">
              <button className="btn-remove-discount" onClick={handleRemoveDiscount}>
                ✕ Remove
              </button>
              <button className="btn-done-discount" onClick={handleSaveDiscount}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SERVICE EDIT MODAL */}
      {editingServiceIdx !== null && currentPackage.services[editingServiceIdx] && (
        <div className="service-edit-overlay">
          <div className="service-edit-modal">
            <div className="edit-modal-content">
              <h3 className="edit-service-title">{currentPackage.services[editingServiceIdx].name}</h3>
              <p className="edit-service-description">Adjust quantity and unit rate to calculate line total</p>

              <div className="edit-form-group">
                <label>QUANTITY ({currentPackage.services[editingServiceIdx].unit || 'EA'})</label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  inputMode="decimal"
                  value={editingServiceQuantity}
                  onChange={(e) => setEditingServiceQuantity(e.target.value)}
                  className="edit-input"
                />
              </div>

              <div className="edit-form-group">
                <label>UNIT RATE</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={editingServiceRate}
                  onChange={(e) => setEditingServiceRate(e.target.value)}
                  className="edit-input"
                  placeholder="0.00"
                />
              </div>

              <div className="edit-line-total">
                <span>Line total</span>
                <span className="edit-total-amount">
                  ${((parseFloat(editingServiceQuantity) || 0) * (parseFloat(editingServiceRate) || 0)).toFixed(2)}
                </span>
              </div>
            </div>

            <div className="edit-modal-footer">
              <button
                className="btn-remove-from-edit"
                onClick={handleRemoveServiceFromEdit}
              >
                ✕ Remove
              </button>
              <button
                className="btn-done-edit"
                onClick={handleSaveServiceEdit}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* INSPECTION PHOTOS — to point at while pricing with the customer */}
      {showGallery && (
        <div className="gallery-overlay" onClick={(e) => e.target === e.currentTarget && setShowGallery(false)}>
          <div className="gallery-sheet">
            <div className="gallery-head">
              <h3>Inspection photos</h3>
              <button type="button" className="btn-close-catalog" onClick={() => setShowGallery(false)} aria-label="Close">✕</button>
            </div>
            {photos.length === 0 ? (
              <p className="gallery-empty">No photos taken in this inspection yet.</p>
            ) : (
              <div className="gallery-body">
                {[...new Set(photos.map((p) => p.section))].map((section) => (
                  <div key={section} className="gallery-group">
                    <div className="gallery-label">{section}</div>
                    <div className="gallery-grid">
                      {photos.filter((p) => p.section === section).map((p, i) => (
                        <img key={i} src={p.src} alt={section} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SERVICE CATALOG MODAL */}
      {showServiceCatalog && (
        <div className="service-catalog-overlay">
          <div className="service-catalog-modal">
            <div className="catalog-header">
              <h3>Service Catalog</h3>
              <button
                className="btn-close-catalog"
                onClick={handleCloseServiceCatalog}
                title="Close catalog"
              >
                ✕
              </button>
            </div>

            <div className="catalog-search">
              <input
                type="text"
                placeholder="Search services..."
                className="catalog-search-input"
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
              />
              {CATALOG_SOURCE !== 'zoho' && <div className="catalog-note">Sample list — Zoho isn’t connected here</div>}
              {catalog.error && <div className="catalog-note">{catalog.items.length ? 'Showing the last saved list — ' : ''}{catalog.error}</div>}
            </div>

            <div className="catalog-services">
              {catalog.status === 'loading' && !catalog.items.length && <div className="catalog-empty">Loading services…</div>}
              {catalog.items.length > 0 && groupCatalog(catalog.items, catalogSearch).length === 0 && (
                <div className="catalog-empty">No service matches “{catalogSearch}”</div>
              )}
              {groupCatalog(catalog.items, catalogSearch).map(({ category, groups }) => (
                <div key={category} className="catalog-category">
                  <div className="catalog-category-name">{category}</div>
                  {groups.map(({ subGroup, items }) => (
                    <div key={subGroup || '-'} className="catalog-subgroup">
                      {subGroup && <div className="catalog-subgroup-name">{subGroup}</div>}
                      {items.map((item) => (
                        <div key={item.id} className="catalog-service-item">
                          <div className="service-details">
                            <div className="service-name">{item.name}</div>
                            <div className="service-price">{priceLabel(item)}</div>
                          </div>
                          <button
                            className="btn-add-service-catalog"
                            aria-label={`Add ${item.name}`}
                            onClick={() => {
                              handleAddServiceToPackage(item);
                              handleCloseServiceCatalog();
                            }}
                          >
                            +
                          </button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotePricingEditor;
